import { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.scss';
import Home from './pages/Home';
import GradientLogo from './components/GradientLogo';
import './utils/rightClick.js';
import { fetchPortfolioContent } from './utils/sanityPortfolioContent';


function App() {
  const [projectsData, setProjectsData] = useState([]);
  const [aboutProject, setAboutProject] = useState(null);
  const [shaderOn, setShaderOn] = useState(false);
  const mousePosRef = useRef({ x: 0.5, y: 0.5 });
  const audioCtxRef = useRef(null);
  const noiseSourceRef = useRef(null);
  const gainNodeRef = useRef(null);
  const filterRef = useRef(null);
  const filter2Ref = useRef(null);

  const getFilterValues = useCallback((nx, ny) => {
    const freq1 = 300 * Math.pow(5000 / 300, nx);
    const freq2 = 800 * Math.pow(8000 / 800, 1 - ny);
    const distFromCenter = Math.hypot(nx - 0.5, ny - 0.5) * 2;
    const q = 8 + (1 - distFromCenter) * 30;

    return { freq1, freq2, q };
  }, []);

  const handleToggleShader = useCallback((event) => {
    if (event?.clientX != null && event?.clientY != null) {
      mousePosRef.current = {
        x: event.clientX / window.innerWidth,
        y: event.clientY / window.innerHeight,
      };
    }

    setShaderOn(prev => !prev);
  }, [])

  useEffect(() => {
    if (shaderOn) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;

      const bufferSize = ctx.sampleRate * 2;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      noiseSourceRef.current = source;

      // Bandpass filter 1 — metallic resonance, driven by mouse X
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.Q.value = 18;
      filterRef.current = filter;

      // Bandpass filter 2 — higher harmonic, driven by mouse Y
      const filter2 = ctx.createBiquadFilter();
      filter2.type = 'bandpass';
      filter2.Q.value = 12;
      filter2Ref.current = filter2;

      const { x, y } = mousePosRef.current;
      const { freq1, freq2, q } = getFilterValues(x, y);
      filter.frequency.value = freq1;
      filter2.frequency.value = freq2;
      filter.Q.value = q;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.3);
      gainNodeRef.current = gain;

      // source → filter1 → gain → out
      // source → filter2 → gain → out  (parallel metallic layers)
      source.connect(filter);
      source.connect(filter2);
      filter.connect(gain);
      filter2.connect(gain);
      gain.connect(ctx.destination);
      source.start();

      const handleMouseMove = (e) => {
        if (!audioCtxRef.current || !filterRef.current || !filter2Ref.current) return;
        const nx = e.clientX / window.innerWidth;   // 0–1 left→right
        const ny = e.clientY / window.innerHeight;  // 0–1 top→bottom
        mousePosRef.current = { x: nx, y: ny };

        // X controls the main resonant frequency (300 Hz – 5000 Hz, log scale)
        const { freq1, freq2, q } = getFilterValues(nx, ny);
        filterRef.current.frequency.setTargetAtTime(freq1, audioCtxRef.current.currentTime, 0.05);

        // Y controls the upper harmonic (800 Hz – 8000 Hz, log scale, inverted so top = high)
        filter2Ref.current.frequency.setTargetAtTime(freq2, audioCtxRef.current.currentTime, 0.05);

        // Q (resonance) increases as you move toward the center of the screen
        filterRef.current.Q.setTargetAtTime(q, audioCtxRef.current.currentTime, 0.08);
      };

      window.addEventListener('mousemove', handleMouseMove);
      // store cleanup fn on the ref so the else-branch can reach it
      audioCtxRef.current._cleanupMouseMove = () => window.removeEventListener('mousemove', handleMouseMove);
    } else {
      if (gainNodeRef.current && audioCtxRef.current) {
        audioCtxRef.current._cleanupMouseMove?.();
        const gain = gainNodeRef.current;
        const ctx = audioCtxRef.current;
        gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
        setTimeout(() => {
          noiseSourceRef.current?.stop();
          ctx.close();
          noiseSourceRef.current = null;
          gainNodeRef.current = null;
          audioCtxRef.current = null;
          filterRef.current = null;
          filter2Ref.current = null;
        }, 350);
      }
    }

    return () => {
      audioCtxRef.current?._cleanupMouseMove?.();
      noiseSourceRef.current?.stop();
      audioCtxRef.current?.close();
      noiseSourceRef.current = null;
      gainNodeRef.current = null;
      audioCtxRef.current = null;
      filterRef.current = null;
      filter2Ref.current = null;
    };
  }, [getFilterValues, shaderOn]);

  useEffect(() => {
    let isMounted = true;

    const loadPortfolioContent = async () => {
      try {
        const { aboutProject: aboutEntry, projects } = await fetchPortfolioContent();

        if (!isMounted) return;

        setProjectsData(projects);
        setAboutProject(aboutEntry);

      } catch (error) {
        console.error('Error fetching Sanity data:', error);
      }
    };

    loadPortfolioContent();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && shaderOn) {
        setShaderOn(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [shaderOn]);

  return (
    <Router>
      <div>
        <Routes>
          <Route path="/" element={<Home projects={projectsData} aboutProject={aboutProject} shaderOn={shaderOn} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <GradientLogo onToggle={handleToggleShader} />
    </Router>
  );
}

export default App;
